import React, { useState, useEffect, useContext } from 'react';
import {
  IonContent,
  IonPage,
  IonCard,
  IonCardContent,
  IonButton,
  IonLoading,
  IonRadioGroup,
  IonLabel,
  IonItem,
  IonRadio
} from '@ionic/react';
import './Summary.scss';
import { AppointmentStepProps } from '../../navigators/AppointmentNavigator';
import Stepper from '../../components/Stepper';
import AppHeader from '../../components/AppHeader';
import { connect } from 'react-redux';
import { AppointmentsRootState } from '../../store/reducers/appointments';
import {
  submitAppointment,
  setAppointment
} from '../../store/actions/appointments';
import { Appointment } from '../../models';
import { Trans } from 'react-i18next';
import AppointmentRepository from '../../repositories/AppointmentRepository';
import { ThemeContext } from '../../components/theme/ThemeProvider';
import EventBus from '../../services/EventBus';

const mapStateToProps = (
  state: { appointments: AppointmentsRootState },
  ownProps: any
) => {
  return {
    appointment: new Appointment(state.appointments.appointment),
    optionalForms: state.appointments.optionalForms
  };
};

const mapDispatchToProps = (dispatch: Function, ownProps: any) => ({
  setAppointment: (appointment: Partial<Appointment>) =>
    dispatch(setAppointment(appointment)),
  submitAppointment: ({ partnershipId }: { partnershipId: string }) =>
    dispatch(submitAppointment({ partnershipId }))
});

type Props = ReturnType<typeof mapDispatchToProps> &
  ReturnType<typeof mapStateToProps> &
  AppointmentStepProps & {
    skipSummary: boolean;
    automaticCheckin: boolean;
  };

const Summary: React.FC<Props> = ({
  step,
  appointment,
  setAppointment,
  submitAppointment,
  history,
  skipSummary = false,
  nextStepRouterLink,
  automaticCheckin = false,
  stepperComponent = <Stepper step={step} />,
  optionalForms
}) => {
  const theme = useContext(ThemeContext);
  const [resultType, setResultType] = useState<string>(
    appointment.laboratory ? appointment.laboratory.available_results[0] : ''
  );
  const [isLoading, setIsLoading] = useState(false);
  let mounted = true;

  const confirmAppointment = () => {
    setIsLoading(true);
    return submitAppointment({ partnershipId: theme.id })
      .then(async (appointment: Appointment) => {
        if (automaticCheckin) {
          await AppointmentRepository.checkin([appointment]);
        }

        if (Object.values(optionalForms).length) {
          const datas = Object.values(optionalForms).flat();
          await AppointmentRepository.postOptionalForm({
            data: datas,
            appointment
          }).catch(() => {
            EventBus.error(
              "Impossible d'envoyer la fiche de renseignement. Veuillez vous adressez au personnel d'accueil."
            );
          });
        }

        if (nextStepRouterLink) {
          history.replace(nextStepRouterLink);
        }
      })
      .catch(() => {
        if (skipSummary) {
          history.replace('/sidep/connexion');
        }
      })
      .finally(() => {
        if (mounted) {
          setIsLoading(false);
        }
      });
  };

  const setApp = () => {
    if (skipSummary) {
      confirmAppointment();
    }

    setAppointment({ results: resultType });

    return function cleanup() {
      mounted = false;
    };
  };

  useEffect(setApp, []);

  return (
    <IonPage>
      <IonLoading isOpen={isLoading} />
      <AppHeader />
      {stepperComponent}
      <IonContent>
        {!skipSummary ? (
          <div className="wrapper wrapper--centered wrapper--stretch ion-padding">
            {appointment.patient ? (
              <IonCard className="card-no-margin summary-card">
                <IonCardContent className="summary-card__header">
                  <small>Qui&nbsp;?</small>
                </IonCardContent>
                <IonCardContent>
                  {appointment.patient?.getFullName()}
                </IonCardContent>
              </IonCard>
            ) : null}
            {appointment.laboratory ? (
              <IonCard className="card-no-margin summary-card">
                <IonCardContent className="summary-card__header">
                  <small>Où&nbsp;?</small>
                </IonCardContent>
                <IonCardContent className="summary-card__content">
                  {appointment.laboratory?.name}
                  <br />
                  <small>{appointment.laboratory?.address}</small>
                </IonCardContent>
              </IonCard>
            ) : null}
            {appointment.slot?.start ? (
              <IonCard className="card-no-margin summary-card">
                <IonCardContent className="summary-card__header">
                  <small>Quand&nbsp;?</small>
                </IonCardContent>
                <IonCardContent className="summary-card__content">
                  Le {appointment.getDayString()}
                  <br />à {appointment.getHourString()}
                </IonCardContent>
              </IonCard>
            ) : null}
            {appointment.documents ? (
              <IonCard className="card-no-margin summary-card">
                <IonCardContent className="summary-card__header">
                  <small>
                    Fichier{appointment.documents.length >= 2 ? 's' : null}
                  </small>
                </IonCardContent>
                <IonCardContent className="summary-card__content">
                  {appointment.documents.map((document) => (
                    <div key={document.id}>
                      <small>{document.name}</small>
                    </div>
                  ))}
                </IonCardContent>
              </IonCard>
            ) : null}
            {appointment.laboratory?.available_results ? (
              <IonCard className="card-no-margin summary-card">
                <IonCardContent className="summary-card__header">
                  <small>Comment recevoir mes résultats&nbsp;?</small>
                </IonCardContent>
                <IonCardContent className="summary-card__content summary-card__content--no-padding ion-align-self-center">
                  <IonRadioGroup
                    value={resultType}
                    onIonChange={(e) => setResultType(e.detail.value)}
                  >
                    {appointment.laboratory?.available_results.map((result) => (
                      <IonItem
                        key={result}
                        lines={
                          appointment.laboratory?.available_results &&
                          appointment.laboratory?.available_results.length > 1
                            ? 'full'
                            : 'none'
                        }
                      >
                        <IonLabel>
                          <Trans i18nKey={`${result}SummarySendResultBy`}>
                            Réception des résultats via un lien envoyé par{' '}
                            {result}
                          </Trans>
                        </IonLabel>
                        <IonRadio slot="start" value={result} />
                      </IonItem>
                    ))}
                  </IonRadioGroup>
                </IonCardContent>
              </IonCard>
            ) : null}
            <div className="ion-text-center">
              <IonButton
                expand="block"
                onClick={() => confirmAppointment()}
                disabled={
                  appointment.laboratory?.available_results && !resultType
                }
              >
                Valider ma demande
              </IonButton>
              <IonButton fill="clear" routerLink="/">
                Annuler
              </IonButton>
            </div>
          </div>
        ) : null}
      </IonContent>
    </IonPage>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(Summary);
